import type { HandleBounds, XYPosition } from '../types';
import type { HandleRef } from './connection';

/** A handle a connection may snap to, with its centre in flow space. */
export type HandleCandidate = HandleRef & XYPosition;

export type CandidateNode = { id: string; positionAbsolute: XYPosition; handleBounds: HandleBounds | null };

const HALF = 0.5;

/** Centres of every measured handle except the one the connection starts from. */
export const getHandleCandidates = (nodes: readonly CandidateNode[], from: HandleRef): HandleCandidate[] =>
  nodes.flatMap(({ id, positionAbsolute, handleBounds }) =>
    [...(handleBounds?.source ?? []), ...(handleBounds?.target ?? [])]
      .filter((handle) => !(id === from.nodeId && handle.id === from.handleId && handle.type === from.type))
      .map((handle) => ({
        nodeId: id,
        handleId: handle.id,
        type: handle.type,
        x: positionAbsolute.x + handle.x + handle.width * HALF,
        y: positionAbsolute.y + handle.y + handle.height * HALF,
      })),
  );

/**
 * Nearest candidate within `radius` of `point` (flow units). On a tie a target handle wins, then a valid one —
 * the v11 order for handles stacked on top of each other.
 */
export const getClosestHandle = (
  point: XYPosition,
  candidates: readonly HandleCandidate[],
  radius: number,
  isValid: (candidate: HandleCandidate) => boolean,
): HandleCandidate | null => {
  let closest: HandleCandidate[] = [];
  let minDistance = Infinity;
  candidates.forEach((candidate) => {
    const distance = Math.hypot(candidate.x - point.x, candidate.y - point.y);
    if (distance > radius || distance > minDistance) return;
    closest = distance < minDistance ? [candidate] : [...closest, candidate];
    minDistance = distance;
  });
  if (closest.length <= 1) return closest[0] ?? null;
  return closest.find((candidate) => candidate.type === 'target') ?? closest.find(isValid) ?? closest[0];
};
